import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material';
import Typography from '@mui/material/Typography';
import Column from 'components/Layout/Column';
import Row from 'components/Layout/Row';
import Avatar from 'further-ui/components/Avatar';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import {
  numberToCurrencyString,
  numberToPercentageString,
  pluralize,
} from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { CommitmentData } from '../../types';

const PaddedRow = styled(Row)(({ theme }) => ({
  paddingLeft: theme.spacing(theme.spacingSizes.xxl),
}));

const AssetRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs, 0),
}));

type Props = Pick<CommitmentData, 'currencySettings'> & {
  holdingsByClass: CommitmentData['realisedHoldings']['byClass'];
};

const RealisedHoldingsByClass: React.FC<Props> = ({
  currencySettings,
  holdingsByClass: { data, totals },
}) => {
  const { currencyLcy } = currencySettings;
  const { toggleRowExpansion, expandedRows } = useExpandedRows();

  const columns: Array<TableColumn<(typeof data)[number]>> = [
    {
      label: 'Asset',
      key: 'assetName',
      paddingY: 0,
      width: 240,
      render: ({ assetImage, assetName }) => (
        <AssetRow centered spacing="xs">
          <Avatar color="random" text={assetName} size="lg" src={assetImage} />
          <Typography variant="inherit">{assetName}</Typography>
        </AssetRow>
      ),
      footer: (
        <>
          Total from {data.length} {pluralize('class', data.length, 'classes')}
        </>
      ),
    },
    {
      label: 'Share class',
      key: 'shareClassName',
      width: 200,
      render: ({ shareClassName }) => shareClassName,
    },
    {
      label: 'Realisation date',
      key: 'realisationDate',
      width: 140,
      render: ({ realisationDate }) =>
        realisationDate.toLabel(undefined, 'MMM dd, yyyy'),
    },
    {
      label: `Invested capital ${currencyLcy}`,
      key: 'investedCapitalLcy',
      width: 180,
      render: ({ investedCapitalLcy }) =>
        numberToCurrencyString(investedCapitalLcy, {
          currency: currencyLcy,
        }),
      footer: numberToCurrencyString(totals.investedCapitalLcy, {
        currency: currencyLcy,
      }),
    },
    {
      label: `Realisation value ${currencyLcy}`,
      key: 'realisationValueLcy',
      width: 180,
      render: ({ realisationValueLcy }) =>
        numberToCurrencyString(realisationValueLcy, {
          currency: currencyLcy,
        }),
      footer: numberToCurrencyString(totals.realisationValueLcy, {
        currency: currencyLcy,
      }),
    },
    {
      label: 'Gross MOIC',
      key: 'grossMoic',
      width: 120,
      render: ({ grossMoic }) => `${grossMoic}x`,
      footer: `${totals.grossMoic}x`,
    },
    {
      label: 'Gross IRR',
      key: 'grossIrr',
      width: 120,
      render: ({ grossIrr }) =>
        numberToPercentageString(grossIrr, { accuracy: 2 }),
      footer: numberToPercentageString(totals.grossIrr, {
        accuracy: 2,
      }),
    },
    {
      key: 'actions',
      width: 50,
      render: (row) =>
        expandedRows.has(row.id) ? (
          <ExpandLess fontSize="small" />
        ) : (
          <ExpandMore fontSize="small" />
        ),
    },
  ];

  const subRowColumns: Array<
    TableColumn<(typeof data)[number]['investments'][number]>
  > = [
    {
      key: columns[0].key,
      width: columns[0].width,
      render: () => <PaddedRow spacing="xs" />,
    },
    {
      key: columns[1].key,
      width: columns[1].width,
      render: () => <PaddedRow spacing="xs" />,
    },
    {
      key: columns[2].key,
      width: columns[2].width,
      render: ({ realisationDate }) =>
        realisationDate.toLabel(undefined, 'MMM dd, yyyy'),
    },
    {
      key: columns[3].key,
      width: columns[3].width,
      render: ({ investedCapitalLcy }) =>
        numberToCurrencyString(investedCapitalLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: columns[4].key,
      width: columns[4].width,
      render: ({ realisationValueLcy }) =>
        numberToCurrencyString(realisationValueLcy, {
          currency: currencyLcy,
        }),
    },
    {
      key: columns[5].key,
      width: columns[5].width,
      render: ({ grossMoic }) => `${grossMoic}x`,
    },
    {
      key: columns[6].key,
      width: columns[6].width,
      render: ({ grossIrr }) =>
        numberToPercentageString(grossIrr, { accuracy: 2 }),
    },
    {
      key: columns[7].key,
      width: 50,
      render: () => null,
    },
  ];

  return (
    <Column spacing="md">
      <Table
        isExpandedRow={(row) => expandedRows.has(row.id)}
        getExpandedRows={(row) =>
          row.investments.map((investment, rowIndex) => ({
            key: investment.id,
            columns: subRowColumns.map((col) => ({
              key: col.key,
              content: col.render ? col.render(investment, rowIndex) : null,
            })),
          }))
        }
        scrollable
        emptyMessage="No holdings"
        columns={columns}
        rows={data}
        onRowClick={(row) => toggleRowExpansion(row.id)}
      />
    </Column>
  );
};

export default RealisedHoldingsByClass;
